import React, { useEffect, useState } from 'react';
import LeasingSimulator from '../components/LeasingSimulator';
import PhoneSection from '../components/PhoneSection';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage, useAuth } from '../App';
import { useTheme } from '../theme';
import { apiUrl, fileUrl, imageUrl } from '../api';

function formatNumberSpaces(num) {
  if (num === undefined || num === null) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// const iconStyle = {
//   width: 28,
//   height: 28,
//   marginRight: 8,
//   verticalAlign: 'middle',
//   filter: 'drop-shadow(0 2px 4px #0006)',
// };

export default function CarDetails() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, idx: 0 });
  const [dotariOpen, setDotariOpen] = useState(false);
  const touchStartX = React.useRef(null);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const fromBuyBackAdmin = Boolean(location?.state?.fromBuyBackAdmin);
  const { token } = useAuth();
  const isAuth = !!token; // admin logged-in indicator
  const { tokens, themeKey, squareTiles } = useTheme();

  // Radius helper now derives directly from global squareTiles
  const radius = React.useCallback((n) => (squareTiles ? 0 : n), [squareTiles]);

  // Pick themed button image for "Cere evaluare" (red/blue), otherwise fallback
  const evalButtonSrc = React.useMemo(() => {
    const map = {
      redLight: 'red theme.png',
      blueLight: 'blue theme.png',
    };
    const name = map[themeKey];
    if (name) return fileUrl(`/assets/buttons/${encodeURIComponent(name)}`);
    // Fallback to existing public asset
    return '/Buton cere Evaluare.png';
  }, [themeKey]);

  const t = {
    en: {
      back: 'Back to Listings',
      price: 'Price',
      year: 'Year',
      model: 'Model',
      type: 'Type',
      km: 'Km',
      fuel: 'Fuel',
      power: 'Power',
      gearbox: 'Gearbox',
      capacity: 'Capacity',
      seller: 'Seller',
      description: 'Description',
      allDetails: 'All details',
    },
    ro: {
      back: 'Înapoi la Anunțuri',
      price: 'Preț',
      year: 'An',
      model: 'Model',
      type: 'Tip',
      km: 'Km',
      fuel: 'Combustibil',
      power: 'Putere',
      gearbox: 'Cutie',
      capacity: 'Capacitate',
      seller: 'Vânzător',
      description: 'Descriere',
      allDetails: 'Detalii complete',
    },
  };
  const tr = t[language];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 700;
  // Consistent label width for detail rows; larger indent on mobile
  const labelMinWidth = isMobile ? 190 : 160;

  useEffect(() => {
    fetch(apiUrl(`/api/cars/${id}`))
      .then(res => res.json())
      .then(data => setCar(data));
  }, [id]);

  // Normalize car fields to handle different backend property names
  function firstOf(c, keys) {
    for (const k of keys) {
      if (c[k] !== undefined && c[k] !== null && c[k] !== '') return c[k];
    }
    return '';
  }

  function normalizeCar(c) {
    if (!c) return null;
    const title = firstOf(c, ['title', 'titlu', 'nume', 'name']);
    const brand =
      firstOf(c, ['marca', 'Marca', 'brand', 'Brand', 'make', 'Make']) ||
      (title ? title.split(' ')[0] : '');
    const model =
      firstOf(c, ['model', 'Model', 'modelul', 'denumireModel']) ||
      (title ? title.split(' ').slice(1).join(' ') : '');
    const year = firstOf(c, [
      'year',
      'Year',
      'an',
      'An',
      'anul',
      'an_fabricatie',
      'fabricationYear',
      'An fabricatie',
      'An de fabricatie',
      'anFabricatie',
      'fabricatie',
    ]);
    const price = firstOf(c, [
      'price',
      'pret',
      'prix',
      'Price',
      'Pret',
      'preț',
      'Preț',
    ]);
    const km = firstOf(c, [
      'km',
      'KM',
      'kms',
      'kilometraj',
      'Kilometraj',
      'kilometri',
      'kilometre',
      'mileage',
      'Mileage',
      'rulaj',
      'Rulaj',
    ]);
    const fuel = firstOf(c, [
      'combustibil',
      'Combustibil',
      'fuel',
      'Fuel',
      'tipCombustibil',
      'TipCombustibil',
      'tip combustibil',
      'Tip combustibil',
      'carburant',
      'Carburant',
      'tipCarburant',
    ]);
    const gearboxRaw = firstOf(c, [
      'cutie',
      'Cutie',
      'cutie_viteze',
      'cutieViteze',
      'cutie de viteze',
      'Cutie de viteze',
      'transmisie',
      'Transmisie',
      'tipTransmisie',
      'Tip transmisie',
      'gearbox',
      'Gearbox',
      'transmission',
    ]);
    const gearbox =
      typeof gearboxRaw === 'string' ? gearboxRaw : gearboxRaw?.type || '';
    const capacity = firstOf(c, [
      'capacitate',
      'capacitataCilindrica',
      'capacitateCilindrica',
      'Capacitate cilindrica',
      'Capacitate Cilindrica',
      'cc',
      'cilindree',
      'engine_cc',
      'EngineCapacity',
    ]);
    const powerKw = firstOf(c, ['kw', 'kW', 'putereKw', 'Putere (kW)']);
    let power = firstOf(c, [
      'putere',
      'Putere',
      'Putere (CP)',
      'hp',
      'HP',
      'power',
      'Power',
    ]);
    if (!power && powerKw) {
      const kw = Number(powerKw);
      if (!Number.isNaN(kw)) power = Math.round(kw * 1.36);
    }
    const type = firstOf(c, ['type', 'tip', 'caroserie', 'body', 'bodyType']);
    const seller =
      (c.user && c.user.username) ||
      firstOf(c, ['seller', 'sellerName', 'vanzator', 'Vanzator']);
    const displayTitle =
      `${brand || ''} ${model || ''}`.trim() || title || '—';

    return {
      brand,
      model,
      year,
      price,
      km,
      fuel,
      gearbox,
      capacity,
      power,
      type,
      seller,
      displayTitle,
    };
  }

  // Build a list of all details to render, excluding internal fields
  function buildAllDetails(c) {
    if (!c) return [];
    const skip = new Set([
      'images',
      'image',
      '_id',
      'id',
      '__v',
      'user',
      'userId',
      'owner',
      'ownerId',
      'createdAt',
      'updatedAt',
      'displayTitle',
      // Hide from All details per request
      'status',
      'sold',
      'descriere',
      'description',
      'dotari',
    ]);
    const labelMap = {
      marca: { ro: 'Marcă', en: 'Brand' },
      Marca: { ro: 'Marcă', en: 'Brand' },
      brand: { ro: 'Marcă', en: 'Brand' },
      Brand: { ro: 'Marcă', en: 'Brand' },
      model: { ro: 'Model', en: 'Model' },
      Model: { ro: 'Model', en: 'Model' },
      year: { ro: 'An', en: 'Year' },
      Year: { ro: 'An', en: 'Year' },
      an: { ro: 'An', en: 'Year' },
      An: { ro: 'An', en: 'Year' },
      'An fabricatie': { ro: 'An fabricație', en: 'Year' },
      'An de fabricatie': { ro: 'An fabricație', en: 'Year' },
      price: { ro: 'Preț', en: 'Price' },
      Price: { ro: 'Preț', en: 'Price' },
      pret: { ro: 'Preț', en: 'Price' },
      Pret: { ro: 'Preț', en: 'Price' },
      km: { ro: 'Kilometraj', en: 'Mileage' },
      KM: { ro: 'Kilometraj', en: 'Mileage' },
      kms: { ro: 'Kilometraj', en: 'Mileage' },
      kilometraj: { ro: 'Kilometraj', en: 'Mileage' },
      Kilometraj: { ro: 'Kilometraj', en: 'Mileage' },
      rulaj: { ro: 'Rulaj', en: 'Mileage' },
      Rulaj: { ro: 'Rulaj', en: 'Mileage' },
      fuel: { ro: 'Combustibil', en: 'Fuel' },
      Fuel: { ro: 'Combustibil', en: 'Fuel' },
      combustibil: { ro: 'Combustibil', en: 'Fuel' },
      Combustibil: { ro: 'Combustibil', en: 'Fuel' },
      carburant: { ro: 'Carburant', en: 'Fuel' },
      Carburant: { ro: 'Carburant', en: 'Fuel' },
      tipCombustibil: { ro: 'Combustibil', en: 'Fuel' },
      TipCombustibil: { ro: 'Combustibil', en: 'Fuel' },
      'Tip combustibil': { ro: 'Combustibil', en: 'Fuel' },
      cutie: { ro: 'Cutie de viteze', en: 'Gearbox' },
      Cutie: { ro: 'Cutie de viteze', en: 'Gearbox' },
      cutie_viteze: { ro: 'Cutie de viteze', en: 'Gearbox' },
      cutieViteze: { ro: 'Cutie de viteze', en: 'Gearbox' },
      'Cutie de viteze': { ro: 'Cutie de viteze', en: 'Gearbox' },
      transmisie: { ro: 'Transmisie', en: 'Transmission' },
      Transmisie: { ro: 'Transmisie', en: 'Transmission' },
      transmission: { ro: 'Transmisie', en: 'Transmission' },
      type: { ro: 'Tip', en: 'Type' },
      tip: { ro: 'Tip', en: 'Type' },
      caroserie: { ro: 'Caroserie', en: 'Body type' },
      bodyType: { ro: 'Caroserie', en: 'Body type' },
      capacitate: { ro: 'Capacitate cilindrică (cc)', en: 'Engine capacity (cc)' },
      capacitateCilindrica: {
        ro: 'Capacitate cilindrică (cc)',
        en: 'Engine capacity (cc)',
      },
      'Capacitate cilindrica': {
        ro: 'Capacitate cilindrică (cc)',
        en: 'Engine capacity (cc)',
      },
      cc: { ro: 'Capacitate (cc)', en: 'Capacity (cc)' },
      cilindree: { ro: 'Cilindree', en: 'Displacement' },
      putere: { ro: 'Putere (CP)', en: 'Power (HP)' },
      Putere: { ro: 'Putere (CP)', en: 'Power (HP)' },
      hp: { ro: 'Putere (CP)', en: 'Power (HP)' },
      HP: { ro: 'Putere (CP)', en: 'Power (HP)' },
      kW: { ro: 'Putere (kW)', en: 'Power (kW)' },
      kw: { ro: 'Putere (kW)', en: 'Power (kW)' },
      culoare: { ro: 'Culoare', en: 'Color' },
      Culoare: { ro: 'Culoare', en: 'Color' },
      numarUsi: { ro: 'Număr uși', en: 'Doors' },
      locuri: { ro: 'Număr locuri', en: 'Seats' },
      VIN: { ro: 'Serie șasiu (VIN)', en: 'VIN' },
      vin: { ro: 'Serie șasiu (VIN)', en: 'VIN' },
        seriaSasiu: { ro: 'Serie Sasiu', en: 'VIN' },
      normă: { ro: 'Normă euro', en: 'Euro norm' },
      euroNorm: { ro: 'Normă euro', en: 'Euro norm' },
  normaPoluare: { ro: 'Normă de poluare', en: 'Emission standard' },
      consum: { ro: 'Consum', en: 'Consumption' },
      descriere: { ro: 'Descriere', en: 'Description' },
      description: { ro: 'Descriere', en: 'Description' },
  dotari: { ro: 'Dotări', en: 'Features' },
      seller: { ro: 'Vânzător', en: 'Seller' },
      vanzator: { ro: 'Vânzător', en: 'Seller' },
      Vanzator: { ro: 'Vânzător', en: 'Seller' },
    };

    const entries = [];
    for (const [key, val] of Object.entries(c)) {
      if (skip.has(key)) continue;
      if (val === undefined || val === null || val === '') continue;
      let displayValue = val;

      if (Array.isArray(val)) {
        displayValue = val.join(', ');
      } else if (typeof val === 'object') {
        const simple = Object.entries(val)
          .filter(([k, v]) => typeof v !== 'object')
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        displayValue = simple || JSON.stringify(val);
      }

      const keyLower = key.toLowerCase();
      if (['price', 'pret', 'preț'].includes(keyLower))
        displayValue = `${formatNumberSpaces(displayValue)} €`;
      if (
        ['km', 'kms', 'kilometraj', 'kilometre', 'kilometri', 'rulaj'].includes(
          keyLower
        )
      )
        displayValue = `${formatNumberSpaces(displayValue)} km`;

      const labelDef = labelMap[key];
      const label = labelDef
        ? language === 'ro'
          ? labelDef.ro
          : labelDef.en
        : key;
      entries.push([label, displayValue]);
    }

    const priority = ['Preț', 'Price', 'An', 'Year', 'Kilometraj', 'Mileage'];
    entries.sort(
      (a, b) =>
        (priority.indexOf(a[0]) + 1 || 999) -
        (priority.indexOf(b[0]) + 1 || 999)
    );

    return entries;
  }

  if (!car)
    return (
      <div style={{ color: '#fff', textAlign: 'center', marginTop: 64 }}>
        Loading...
      </div>
    );

  const nc = normalizeCar(car);
  const emission = firstOf(car, [
    'normaPoluare',
    'normă',
    'norma',
    'euroNorm',
    'EuroNorm',
    'normaEuro',
    'euro',
    'Euro',
  ]);

  const images =
    car.images && car.images.length > 0
      ? car.images
      : car.image
      ? [car.image]
      : [];

  // Prepare dotari (features) list for dropdown reveal
  const dotariRaw =
    (car && (car.dotari ?? car.Dotari ?? car.features ?? car.options ?? car.optiuni)) || [];
  const dotariList = Array.isArray(dotariRaw)
    ? dotariRaw
        .filter(Boolean)
        .map(x => String(x).trim())
        .filter(x => x.length > 0)
    : typeof dotariRaw === 'string'
    ? dotariRaw
        .split(/[;\n,]+/)
        .map(s => s.trim())
        .filter(s => s.length > 0)
    : [];

  return (
    <>
    <div
      style={{
        maxWidth: 900,
        margin: '0 auto',
        marginTop: isMobile ? -12 : 0,
        padding: isMobile ? '16px 12px' : '32px 16px',
        background: tokens.surface,
        borderRadius: radius(18),
        boxShadow: `0 6px 32px ${tokens.shadow}`,
        color: tokens.text,
      }}
    >
      <button
        onClick={() => {
          if (fromBuyBackAdmin) {
            // Prefer history back to preserve scroll/filters; fallback to explicit route
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/admin/buyback');
            }
          } else {
            navigate('/');
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          fontFamily:
            'Inter, Montserrat, Nunito, Segoe UI, Roboto, Arial, Helvetica, sans-serif',
          fontWeight: 600,
          letterSpacing: '0.01em',
          padding: '2px 14px',
          marginBottom: 16,
          background: tokens.surface,
          color: tokens.text,
          border: `2px solid ${tokens.accent}`,
          borderRadius: 999,
          fontSize: 13,
          boxShadow: `0 2px 10px ${tokens.shadowSoft}, inset 0 1px 6px ${tokens.shadowSoft}`,
          cursor: 'pointer',
          outline: 'none',
          textShadow: '0 1px 3px #000a',
          transition: 'all 0.18s cubic-bezier(.4,0,.2,1)',
        }}
        onMouseOver={e => {
          e.currentTarget.style.background = tokens.surface;
          e.currentTarget.style.transform = 'scale(1.04)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${tokens.shadow}`;
        }}
        onMouseOut={e => {
          e.currentTarget.style.background = tokens.surface;
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = `0 4px 18px ${tokens.shadowSoft}`;
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }}
        >
          <polyline
            points="15 18 9 12 15 6"
            fill="none"
            stroke={tokens.textMuted}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {fromBuyBackAdmin ? 'Înapoi la buyback' : 'Înapoi la Anunțuri'}
      </button>

      {/* CTA removed from here; moved below phone section */}

      <div
        style={{
          display: 'flex',
          gap: window.innerWidth < 700 ? 8 : 32,
          flexWrap: window.innerWidth < 700 ? 'wrap' : 'nowrap',
        }}
      >
        {/* Image gallery */}
        <div
          style={{
            flex: '1 1 320px',
            minWidth: window.innerWidth < 700 ? '100%' : 320,
            position: 'relative',
          }}
        >
          {images.length > 0 ? (
            <div
              style={{
                position: 'relative',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: window.innerWidth < 700 ? '100%' : 420,
                }}
                onTouchStart={(e) => {
                  if (window.innerWidth < 700) {
                    touchStartX.current = e.touches[0].clientX;
                  }
                }}
                onTouchEnd={(e) => {
                  if (window.innerWidth < 700 && touchStartX.current !== null) {
                    const touchEndX = e.changedTouches[0].clientX;
                    const diff = touchStartX.current - touchEndX;
                    const threshold = 50;
                    if (diff > threshold) {
                      // Swipe left - next image
                      setImgIdx((imgIdx + 1) % images.length);
                    } else if (diff < -threshold) {
                      // Swipe right - previous image
                      setImgIdx((imgIdx - 1 + images.length) % images.length);
                    }
                    touchStartX.current = null;
                  }
                }}
              >
                <img
                  src={imageUrl(images[imgIdx])}
                  alt={car.title}
                  style={{
                    width: '100%',
                    maxWidth: window.innerWidth < 700 ? '100%' : 420,
                    height: window.innerWidth < 700 ? 180 : 280,
                    objectFit: 'cover',
                    borderRadius: radius(16),
                    boxShadow: '0 4px 16px #0008',
                    cursor: 'zoom-in',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                  }}
                  onClick={() => setLightbox({ open: true, idx: imgIdx })}
                  onDragStart={(e) => e.preventDefault()}
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setImgIdx((imgIdx - 1 + images.length) % images.length)
                      }
                      style={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: tokens.panel,
                        color: tokens.text,
                        border: 'none',
                        borderRadius: '50%',
                        width: window.innerWidth < 700 ? 32 : 36,
                        height: window.innerWidth < 700 ? 32 : 36,
                        minWidth: window.innerWidth < 700 ? 32 : 36,
                        minHeight: window.innerWidth < 700 ? 32 : 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: window.innerWidth < 700 ? 18 : 22,
                        cursor: 'pointer',
                        opacity: 0.85,
                        zIndex: 2,
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      ‹
                    </button>
                    <button
                      onClick={() =>
                        setImgIdx((imgIdx + 1) % images.length)
                      }
                      style={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: tokens.panel,
                        color: tokens.text,
                        border: 'none',
                        borderRadius: '50%',
                        width: window.innerWidth < 700 ? 32 : 36,
                        height: window.innerWidth < 700 ? 32 : 36,
                        minWidth: window.innerWidth < 700 ? 32 : 36,
                        minHeight: window.innerWidth < 700 ? 32 : 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: window.innerWidth < 700 ? 18 : 22,
                        cursor: 'pointer',
                        opacity: 0.85,
                        zIndex: 2,
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      ›
                    </button>
                  </>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: window.innerWidth < 700 ? 4 : 8,
                  marginTop: 8,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                {images.map((img, idx) => (
                  <img
                    key={img}
                    src={imageUrl(img)}
                    alt={`${car.title}-${idx}`}
                    style={{
                      width: window.innerWidth < 700 ? 38 : 54,
                      height: window.innerWidth < 700 ? 28 : 40,
                      objectFit: 'cover',
                      borderRadius: radius(6),
                      border:
                        idx === imgIdx
                          ? `2px solid ${tokens.accent}`
                          : `2px solid ${tokens.mutedBorder}`,
                      cursor: 'zoom-in',
                    }}
                    onClick={() => setLightbox({ open: true, idx })}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                width: window.innerWidth < 700 ? '100%' : 420,
                height: window.innerWidth < 700 ? 180 : 280,
                background: tokens.panel,
                borderRadius: radius(16),
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.textMuted,
                fontSize: window.innerWidth < 700 ? 18 : 32,
              }}
            >
              No Photo
            </div>
          )}
        </div>

        {/* Car info */}
        <div style={{ flex: '2 1 400px', minWidth: 320, position: 'relative' }}>
          {/* Share button for desktop: top-right of info panel */}
          {!isMobile && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copiat!');
              }}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                background: tokens.panel,
                color: tokens.accent,
                border: 'none',
                borderRadius: radius(8),
                padding: '6px 14px',
                fontWeight: 600,
                fontSize: 16,
                cursor: 'pointer',
                boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
              }}
              title="Copiază linkul anunțului"
            >
              Share
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', paddingRight: isMobile ? 84 : 0 }}>
            <h2
              style={{
                fontSize: 32,
                fontWeight: 700,
                marginBottom: 0,
                marginTop: 0,
                letterSpacing: 1,
              }}
            >
              {nc.displayTitle}
            </h2>
            {/* Share button for mobile: corner of title row */}
            {isMobile && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copiat!');
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: tokens.panel,
                  color: tokens.accent,
                  border: 'none',
                  borderRadius: radius(8),
                  padding: '6px 12px',
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: 'pointer',
                  boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
                }}
                title="Copiază linkul anunțului"
              >
                Share
              </button>
            )}
          </div>

          <div
            style={{
              fontSize: 18,
              color: tokens.textMuted,
              fontWeight: 700,
              marginLeft: 2,
              marginTop: 6,
            }}
          >
            An: {nc.year || '—'}
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 18,
              alignItems: 'center',
              marginTop: 2,
            }}
          >
            {nc.price && (
              <span
                style={{
                  fontSize: 36,
                  color: tokens.accent,
                  fontWeight: 800,
                }}
              >
                {formatNumberSpaces(nc.price)} €
              </span>
            )}
          </div>

          {/* Phone contact block directly under the price */}
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <PhoneSection />
            {/* Trade-in CTA: friendly copy below phone */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                aria-label="Cere o evaluare pentru trade-in"
                onClick={() => navigate(`/buyback/${id}`)}
                style={{
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  cursor: 'pointer',
                  display: 'inline-block',
                  lineHeight: 0,
                  width: isMobile ? '100%' : 'auto',
                  boxShadow: 'none'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                <img
                  src={evalButtonSrc}
                  alt="Cere o evaluare pentru trade-in"
                  style={{
                    display: 'block',
                    width: isMobile ? '100%' : 360,
                    maxWidth: 420,
                    height: 'auto',
                    borderRadius: 0,
                    background: 'transparent'
                  }}
                />
              </button>
              <span style={{ color: tokens.textMuted, fontSize: 14, maxWidth: 420 }}>
                {language === 'ro' ? 'Vrei să schimbi? Trimite-ne detalii despre mașina ta și îți răspundem rapid.' : 'Want to trade? Send us details about your car and we\'ll respond quickly.'}
              </span>
            </div>
            {/* Generate contract (admin only) */}
            {isAuth && (
              <div style={{ marginTop: 12 }}>
                <button
                  aria-label={language === 'ro' ? 'Generează contract de vânzare-cumpărare pentru această mașină' : 'Generate sale-purchase contract for this car'}
                  title={language === 'ro' ? 'Generează contract pentru această mașină' : 'Generate contract for this car'}
                  onClick={() => {
                    const cid = car?._id || id;
                    navigate('/admin/generate-contract', { state: { preselectedCarId: cid } });
                  }}
                  style={{
                    background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`,
                    color: tokens.inverse,
                    border: 'none',
                    padding: '10px 14px',
                    fontWeight: 800,
                    fontSize: 14,
                    borderRadius: 999,
                    cursor: 'pointer',
                    boxShadow: `0 6px 22px ${tokens.shadow}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    letterSpacing: '.2px'
                  }}
                  onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: '#0b0b0b22'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke={tokens.inverse} strokeWidth="1.8"/>
                      <path d="M14 3v5h5" stroke={tokens.inverse} strokeWidth="1.8"/>
                      <path d="M8 14h8M8 18h8" stroke={tokens.inverse} strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </span>
                  {language === 'ro' ? 'Generează contract' : 'Generate contract'}
                </button>
                <div style={{ color: tokens.textMuted, fontSize: 12, marginTop: 6, marginLeft: 6 }}>
                  {language === 'ro' ? 'pentru această mașină' : 'for this car'}
                </div>
              </div>
            )}
          </div>

          {nc.price && <LeasingSimulator price={Number(nc.price)} compact />}

          <div style={{ marginBottom: 18 }}>{/* Seller hidden */}</div>
        </div>
      </div>

      {/* Dotari dropdown reveal (moved before All details) */}
      <div style={{ marginTop: 16 }}>
        <button
          onClick={() => setDotariOpen(v => !v)}
          aria-expanded={dotariOpen}
          aria-controls="dotari-list"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: tokens.panel,
            color: tokens.text,
            border: `1px solid ${tokens.border}`,
            borderRadius: radius(10),
            padding: '10px 12px',
            cursor: 'pointer',
            boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
          }}
        >
          <span style={{ fontWeight: 700, color: tokens.accent, fontSize: 18 }}>Dotari</span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            style={{
              transition: 'transform .18s',
              transform: dotariOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <path d="M6 9l6 6 6-6" stroke={tokens.textMuted} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {dotariOpen && (
          <div id="dotari-list" style={{ marginTop: 10, background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: radius(10), padding: '8px 12px' }}>
            {dotariList.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 20, color: tokens.text }}>
                {dotariList.map((d, idx) => (
                  <li key={idx} style={{ marginBottom: 6, listStyleType: 'disc', fontSize: 15 }}>{d}</li>
                ))}
              </ul>
            ) : (
              <div style={{ color: tokens.textMuted }}>—</div>
            )}
          </div>
        )}
      </div>

      {/* All details */}
      <div
        style={{
          background: tokens.surface,
          borderRadius: radius(12),
          padding: 18,
          marginTop: 8,
          boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: tokens.accent,
            marginBottom: 12,
          }}
        >
          {tr.allDetails}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 12,
          }}
        >
          {/* Brand / Marcă */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              borderBottom: `1px dashed ${tokens.border}`,
              paddingBottom: 6,
            }}
          >
            <div style={{ width: labelMinWidth, color: tokens.textMuted }}>{language === 'ro' ? 'Marcă' : 'Brand'}:</div>
            <div style={{ color: tokens.text, fontWeight: 600 }}>{nc.brand || '—'}</div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              borderBottom: `1px dashed ${tokens.border}`,
              paddingBottom: 6,
            }}
          >
            <div style={{ width: labelMinWidth, color: tokens.textMuted }}>{tr.model}:</div>
            <div style={{ color: tokens.text, fontWeight: 600 }}>{nc.model}</div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              borderBottom: `1px dashed ${tokens.border}`,
              paddingBottom: 6,
            }}
          >
            <div style={{ width: labelMinWidth, color: tokens.textMuted }}>{tr.fuel}:</div>
            <div style={{ color: tokens.text, fontWeight: 600 }}>{nc.fuel || '—'}</div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              borderBottom: `1px dashed ${tokens.border}`,
              paddingBottom: 6,
            }}
          >
            <div style={{ width: labelMinWidth, color: tokens.textMuted }}>{language === 'ro' ? 'Kilometraj' : 'Mileage'}:</div>
            <div style={{ color: tokens.text, fontWeight: 600 }}>
              {nc.km ? formatNumberSpaces(nc.km) : '—'}
            </div>
          </div>
          {emission && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'baseline',
                borderBottom: `1px dashed ${tokens.border}`,
                paddingBottom: 6,
              }}
            >
              <div style={{ width: labelMinWidth, color: tokens.textMuted }}>
                {language === 'ro' ? 'Normă de poluare' : 'Emission standard'}:
              </div>
              <div style={{ color: tokens.text, fontWeight: 600 }}>{String(emission)}</div>
            </div>
          )}
          {/* Removed title row and duplicate model row */}
          {nc.type && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'baseline',
                borderBottom: `1px dashed ${tokens.border}`,
                paddingBottom: 6,
              }}
            >
              <div style={{ width: labelMinWidth, color: tokens.textMuted }}>{tr.type}:</div>
              <div style={{ color: tokens.text, fontWeight: 600 }}>{nc.type}</div>
            </div>
          )}

          {buildAllDetails(car)
            .filter(
              ([label]) =>
                ![
                  tr.price,
                  tr.year,
                  'Km',
                  'Kilometraj',
                  'Mileage',
                  tr.fuel,
                  'title',
                  tr.model,
                  language === 'ro' ? 'Marcă' : 'Brand',
                  tr.type,
                  'Preț',
                  'An',
                  'Combustibil',
                  'Model',
                  'Marcă',
                  'Brand',
                  'Tip',
                  'Normă de poluare',
                  'Euro norm',
                  'Emission standard',
                ].includes(label)
            )
            .map(([label, value]) => (
              <div
                key={label + String(value)}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'baseline',
                  borderBottom: `1px dashed ${tokens.border}`,
                  paddingBottom: 6,
                }}
              >
                <div style={{ width: labelMinWidth, color: tokens.textMuted }}>{label}:</div>
                <div style={{ color: tokens.text, fontWeight: 600 }}>{String(value)}</div>
              </div>
            ))}
        </div>

      </div>
    </div>
    {/* Lightbox overlay with swipe, arrows (no background), and close X */}
    {lightbox.open && images.length > 0 && (
      <div
        role="dialog"
        aria-modal="true"
        onClick={() => setLightbox({ open: false, idx: 0 })}
        onTouchStart={(e) => { touchStartX.current = e.changedTouches?.[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          const start = touchStartX.current;
          const end = e.changedTouches?.[0]?.clientX ?? null;
          if (start != null && end != null) {
            const dx = end - start;
            const threshold = 40;
            if (dx > threshold) {
              // swipe right -> previous
              setLightbox(lb => ({ ...lb, idx: (lb.idx - 1 + images.length) % images.length }));
            } else if (dx < -threshold) {
              // swipe left -> next
              setLightbox(lb => ({ ...lb, idx: (lb.idx + 1) % images.length }));
            }
          }
          touchStartX.current = null;
        }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setLightbox({ open: false, idx: 0 });
          if (e.key === 'ArrowLeft') setLightbox(lb => ({ ...lb, idx: (lb.idx - 1 + images.length) % images.length }));
          if (e.key === 'ArrowRight') setLightbox(lb => ({ ...lb, idx: (lb.idx + 1) % images.length }));
        }}
        tabIndex={-1}
      >
        {/* Content wrapper to stop propagation */}
        <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close X */}
          <button
            aria-label={language === 'ro' ? 'Închide' : 'Close'}
            onClick={() => setLightbox({ open: false, idx: 0 })}
            style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 6 }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </button>

          <img
            src={imageUrl(images[lightbox.idx])}
            alt="zoom"
            style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: radius(8), boxShadow: '0 10px 40px #000', cursor: 'default' }}
          />

          {/* Arrows without background */}
          {images.length > 1 && (
            <>
              <button
                aria-label="Previous"
                onClick={() => setLightbox(lb => ({ ...lb, idx: (lb.idx - 1 + images.length) % images.length }))}
                style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12l6-6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <button
                aria-label="Next"
                onClick={() => setLightbox(lb => ({ ...lb, idx: (lb.idx + 1) % images.length }))}
                style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </>
          )}
        </div>
      </div>
    )}
  </>
  );
}
